import {
	faCheck,
	faClock,
	faFilter,
	faPaperPlane,
	faPlus,
	faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { FilterBar } from "../../../components/molecule/molecule";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import ProjectHeader from "../../../components/ProjectHeader";
import {
	CATEGORY_ACCESS,
	isCategoryAccessScoped,
	PROJECT_AREA,
	PROJECT_ACTION,
} from "../../../constant/projectPermissions";
import useProjectPermission from "../../../hooks/useProjectPermission";
import isGlobalAdminRole from "../../../utils/isGlobalAdmin";
import {
	useGetProjectMembersQuery,
	usePromoteAdminMutation,
	useRemoveProjectMemberMutation,
	useResendInviteMutation,
	useRevokeProjectMemberMutation,
} from "../../../store/services/projectMembers";
import { useGetProjectTicketCategoriesQuery } from "../../../store/services/ticketCategoriesService";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { createActionColumn } from "../../../utils/grid-columns";
import { getFormattedDate, showToast } from "../../../utils/method";
import { searchData } from "../../../utils/search";
import AddMemberWizard from "./add-member-wizard/add-member-wizard";
import DemoteAdminModal from "./demote-admin-modal";
import EditPermissionsModal from "./edit-permissions-modal";
import InviteProvisionalModal from "./invite-provisional-modal";
import "./project-members.scss";

const isProvisionalMember = (m) =>
	m?.permissions == null && m?.inviteStatus == null;

const MemberNameCell = ({ data }) => {
	const user = data?.user;
	if (!user) {
		return "-";
	}
	const name =
		user.fullName ||
		[user.firstName, user.lastName].filter(Boolean).join(" ") ||
		user.email;
	return (
		<div className="project-members__name-cell">
			<span className="project-members__name">{name}</span>
		</div>
	);
};

// ── Pill primitive ────────────────────────────────────────────────────────
// Read-only status indicator. (For the clickable category-access pill we
// inline a styled <span> directly inside OverlayTrigger so the popover
// handler isn't shadowed.)
const Pill = ({ tone = "muted", icon, title, children }) => (
	<span className={`pm-pill pm-pill--${tone}`} title={title}>
		{icon && <FontAwesomeIcon icon={icon} className="pm-pill__icon" />}
		<span>{children}</span>
	</span>
);

// ── Category-access cell ──────────────────────────────────────────────────
// Strict mapping: ALL → green pill, NONE → muted pill, scoped array → amber
// pill that opens a popover listing the actual accessible categories.
// Anything else (null, undefined, unknown shape) renders blank.
const CategoryAccessCell = ({ data, categoryNamesById }) => {
	const { t } = useTranslation();
	const access = data?.permissions?.ticketCategories?.categoryAccess;

	// Admins have full access and carry no permissions blob.
	if (data?.isProjectAdmin || access === CATEGORY_ACCESS.ALL) {
		return (
			<Pill tone="success" icon={faCheck}>
				{t("projects.permissions.category-access-all")}
			</Pill>
		);
	}
	if (access === CATEGORY_ACCESS.NONE) {
		return (
			<Pill tone="muted">
				{t("projects.permissions.category-access-none")}
			</Pill>
		);
	}
	if (!isCategoryAccessScoped(access)) {
		return null;
	}

	const namesById = categoryNamesById || {};
	const resolved = access.map((id) => namesById[id] || id);
	const count = access.length;
	const popover = (
		<Popover id={`pm-cat-${data.id}`} className="pm-popover">
			<Popover.Header as="h3">
				{t("projects.permissions.category-access-popover-title")}
			</Popover.Header>
			<Popover.Body>
				{resolved.length === 0 ? (
					<em className="text-muted">
						{t("projects.permissions.category-access-empty")}
					</em>
				) : (
					<ul className="pm-popover__list">
						{resolved.map((name, i) => (
							<li key={i}>{name}</li>
						))}
					</ul>
				)}
			</Popover.Body>
		</Popover>
	);

	// Inline the clickable element so OverlayTrigger can attach its handler
	// directly to it (wrapping in <Pill> button + stopPropagation would
	// swallow the click before OverlayTrigger sees it).
	return (
		<OverlayTrigger
			trigger="click"
			rootClose
			placement="bottom"
			overlay={popover}
			container={document.body}
		>
			<span
				className="pm-pill pm-pill--warn pm-pill--clickable"
				role="button"
				tabIndex={0}
				title={t("projects.permissions.category-access-popover-title")}
			>
				<FontAwesomeIcon icon={faFilter} className="pm-pill__icon" />
				<span>
					{t("projects.permissions.category-access-limited", { count })}
				</span>
			</span>
		</OverlayTrigger>
	);
};

// ── Invite-status cell ─────────────────────────────────────────────────────
// ACCEPTED → green check. PENDING → amber clock. No status → "+ Invite to
// project" action (or read-only muted pill if the viewer can't invite).
const InviteStatusCell = ({
	data,
	onInvite,
	onInviteProvisional,
	invitingMemberId,
	canInvite,
}) => {
	const { t } = useTranslation();
	const status = data?.inviteStatus;
	const inviteInFlight = invitingMemberId === data?.id;

	if (status === "ACCEPTED") {
		return (
			<Pill tone="success" icon={faCheck}>
				{t("projects.permissions.invite-status-accepted")}
			</Pill>
		);
	}
	if (status === "PENDING") {
		return (
			<Pill tone="warn" icon={faClock}>
				{t("projects.permissions.invite-status-pending")}
			</Pill>
		);
	}
	const provisional = isProvisionalMember(data);
	const clickHandler = provisional ? onInviteProvisional : onInvite;
	if (canInvite && clickHandler && data?.user?.email) {
		return (
			<button
				type="button"
				className="pm-invite-action"
				onClick={(e) => {
					e.stopPropagation();
					clickHandler(data);
				}}
				disabled={inviteInFlight}
				title={t("projects.members.invite-to-project")}
			>
				<FontAwesomeIcon
					icon={inviteInFlight ? faSpinner : faPlus}
					className={inviteInFlight ? "fa-spin" : ""}
				/>
				<span>{t("projects.members.invite-to-project")}</span>
			</button>
		);
	}
	return (
		<Pill tone="muted" icon={faPaperPlane}>
			{t("projects.permissions.invite-status-not-invited")}
		</Pill>
	);
};

const getMemberColumns = (t, handlers, perm, extras = {}) => [
	{
		field: "createdAt",
		headerName: t("projects.provided"),
		maxWidth: 120,
		valueFormatter: (params) => getFormattedDate(params.value),
	},
	{
		field: "user",
		headerName: t("users.name"),
		minWidth: 200,
		valueGetter: (params) =>
			params.data?.user?.fullName ||
			[params.data?.user?.firstName, params.data?.user?.lastName]
				.filter(Boolean)
				.join(" ") ||
			params.data?.user?.email ||
			"",
		cellRenderer: (params) => <MemberNameCell data={params.data} />,
	},
	{
		field: "user.email",
		headerName: t("users.email"),
		valueGetter: (params) => params.data.user?.email || "",
	},
	{
		field: "user.company.name",
		headerName: t("users.company"),
		valueGetter: (params) => params.data.user?.company?.name || "-",
	},
	{
		field: "presetUsedName",
		headerName: t("projects.members.role"),
		valueGetter: (params) => {
			if (params.data.isProjectAdmin) {
				return t("projects.members.is-project-admin");
			}
			const name = params.data.presetUsedName;
			if (!name) {
				return '';
			}
			return params.data.presetModified ? `${name}*` : name;
		},
	},
	{
		field: "permissions",
		headerName: t("projects.permissions.category-access-scope"),
		sortable: false,
		// valueGetter feeds the text-search index. Mirrors the strict cell
		// logic - unknown/null returns empty so it doesn't match anything.
		valueGetter: (params) => {
			if (params.data?.isProjectAdmin) {
				return t("projects.permissions.category-access-all");
			}
			const access =
				params.data?.permissions?.ticketCategories?.categoryAccess;
			if (access === CATEGORY_ACCESS.ALL) return t("projects.permissions.category-access-all");
			if (access === CATEGORY_ACCESS.NONE) return t("projects.permissions.category-access-none");
			if (isCategoryAccessScoped(access)) {
				return t("projects.permissions.category-access-limited", {
					count: access.length,
				});
			}
			return "";
		},
		cellRenderer: (params) => (
			<CategoryAccessCell
				data={params.data}
				categoryNamesById={extras.categoryNamesById}
			/>
		),
	},
	{
		field: "inviteStatus",
		headerName: t("projects.permissions.invite-status"),
		maxWidth: 180,
		valueGetter: (params) => {
			const status = params.data?.inviteStatus;
			if (status === "PENDING") {
				return t("projects.permissions.invite-status-pending");
			}
			if (status === "ACCEPTED") {
				return t("projects.permissions.invite-status-accepted");
			}
			return t("projects.permissions.invite-status-not-invited");
		},
		cellRenderer: (params) => (
			<InviteStatusCell
				data={params.data}
				onInvite={extras.onInvite}
				onInviteProvisional={extras.onInviteProvisional}
				invitingMemberId={extras.invitingMemberId}
				canInvite={extras.canInvite}
			/>
		),
	},
	createActionColumn({
		t,
		maxWidth: 160,
		actions: [
			{
				onClick: handlers.onInviteProvisional,
				label: "projects.members.invite-to-project",
				show: (row) => extras.canInvite && isProvisionalMember(row),
			},
			{
				onClick: handlers.onResendInvite,
				label: "projects.members.resend-invite",
				show: (row) =>
					perm.isProjectAdmin && row.inviteStatus === "PENDING",
			},
			{
				onClick: handlers.onEditPermissions,
				label: "projects.members.edit-permissions",
				// Admins have full access - their permission blob is meaningless,
				// so manage them via Promote/Demote, not Edit Permissions.
				show: (row) =>
					perm.isProjectAdmin &&
					!row.isProjectAdmin &&
					!isProvisionalMember(row),
			},
			{
				onClick: handlers.onPromoteAdmin,
				label: "projects.members.promote-admin",
				show: (row) =>
					perm.canPromoteAdmin &&
					!row.isProjectAdmin &&
					!isProvisionalMember(row),
			},
			{
				onClick: handlers.onDemoteAdmin,
				label: "projects.members.demote-admin",
				// Can't demote yourself - that would lock you out of member mgmt.
				show: (row) =>
					perm.canPromoteAdmin &&
					row.isProjectAdmin &&
					row.id !== perm.currentMemberId,
			},
			{
				onClick: handlers.onRevoke,
				label: "projects.members.revoke-access",
				show: (row) =>
					perm.isProjectAdmin &&
					!isProvisionalMember(row) &&
					!row.isProjectAdmin,
			},
			{
				onClick: handlers.onDelete,
				label: "projects.members.remove",
				danger: true,
				show: () => perm.isProjectAdmin,
			},
		],
	}),
];

function ProjectMembers({ projectId }) {
	const { t } = useTranslation();
	const paramProjectId = useParams().projectId || projectId;
	const globalRole = useSelector((state) => state.auth?.user?.role);
	const isGlobalAdmin = isGlobalAdminRole(globalRole);

	const {
		can,
		isProjectAdmin,
		currentMember,
		isLoading: permissionLoading,
	} = useProjectPermission();
	const canViewMembers = !permissionLoading && can(PROJECT_AREA.MEMBERS, PROJECT_ACTION.VIEW);
	// All member management (invite / edit-permissions / remove) is
	// Project-Admin-only - never delegatable to a regular member.
	const canInviteMembers = isProjectAdmin;
	// Project admins and global admins can promote/demote co-admins. The backend
	// blocks demoting the last admin, so this can never strand a project.
	const canPromoteAdmin = isProjectAdmin || isGlobalAdmin;

	const memberPerm = useMemo(
		() => ({
			canPromoteAdmin,
			isProjectAdmin,
			currentMemberId: currentMember?.id,
		}),
		[canPromoteAdmin, isProjectAdmin, currentMember?.id],
	);

	const projectDetails = useSelector((state) => state.projectDetails.project);

	const {
		data: projectMembersResponse,
		isLoading,
		isFetching,
		refetch,
	} = useGetProjectMembersQuery(paramProjectId, {
		skip: !paramProjectId || !canViewMembers,
	});
	const projectMembers = useMemo(
		() => projectMembersResponse?.items ?? [],
		[projectMembersResponse?.items],
	);

	const [removeMember] = useRemoveProjectMemberMutation();
	const [revokeMember] = useRevokeProjectMemberMutation();
	const [promoteAdmin] = usePromoteAdminMutation();
	const [resendInvite] = useResendInviteMutation();

	// Used only to resolve category IDs → names in the category-access popover.
	// Endpoint is auth-protected but not project-permission-gated, so it's safe
	// to call from anyone with `members.view`.
	const { data: categoriesData } = useGetProjectTicketCategoriesQuery(
		{ projectId: paramProjectId },
		{ skip: !paramProjectId || !canViewMembers },
	);
	const categoryNamesById = useMemo(() => {
		const map = {};
		(categoriesData?.items ?? []).forEach((cat) => {
			if (cat?.id) map[cat.id] = cat.name;
		});
		return map;
	}, [categoriesData?.items]);

	const [filteredMembers, setFilteredMembers] = useState([]);
	const [showInviteWizard, setShowInviteWizard] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showRevokeModal, setShowRevokeModal] = useState(false);
	const [showDemoteModal, setShowDemoteModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [memberToDelete, setMemberToDelete] = useState(null);
	const [memberToRevoke, setMemberToRevoke] = useState(null);
	const [memberToDemote, setMemberToDemote] = useState(null);
	const [memberToEdit, setMemberToEdit] = useState(null);
	const [memberToInviteProvisional, setMemberToInviteProvisional] =
		useState(null);
	const [showInviteProvisionalModal, setShowInviteProvisionalModal] =
		useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [revokeLoading, setRevokeLoading] = useState(false);
	const [invitingMemberId, setInvitingMemberId] = useState(null);

	const filterConfiguration = useMemo(
		() =>
			getSearchAddFilter(
				{
					addTextKey: "projects.members.invite",
					hideAddButton: !canInviteMembers,
				},
				[
					{
						key: "refresh",
						type: "button",
						labelKey: "common.refresh",
						displayPosition: "right",
						className: "btn btn-outline-secondary me-2",
						icon: "refresh",
						disabled: isFetching,
					},
				],
			),
		[canInviteMembers, isFetching],
	);
	const [filterItems, setFilterItems] = useState(filterConfiguration);

	useEffect(() => {
		setFilterItems(filterConfiguration);
	}, [filterConfiguration]);

	useEffect(() => {
		setFilteredMembers(projectMembers);
	}, [projectMembers]);

	const handleDelete = useCallback((data) => {
		setMemberToDelete(data);
		setShowDeleteModal(true);
	}, []);

	const handleRevoke = useCallback((data) => {
		setMemberToRevoke(data);
		setShowRevokeModal(true);
	}, []);

	const handleEditPermissions = useCallback((data) => {
		setMemberToEdit(data);
		setShowEditModal(true);
	}, []);

	const handlePromoteAdmin = useCallback(
		async (member) => {
			try {
				await promoteAdmin({
					projectId: paramProjectId,
					memberId: member.id,
					isProjectAdmin: true,
				}).unwrap();
				showToast(t("projects.members.promote-admin-success"), {
					type: "success",
				});
			} catch (error) {
				showToast(error?.data?.message || t("errors.general"), {
					type: "error",
				});
			}
		},
		[paramProjectId, promoteAdmin, t],
	);

	const handleDemoteAdmin = useCallback((member) => {
		setMemberToDemote(member);
		setShowDemoteModal(true);
	}, []);

	const handleInviteProvisional = useCallback((member) => {
		setMemberToInviteProvisional(member);
		setShowInviteProvisionalModal(true);
	}, []);

	// Re-issues the invite email for an existing row that hasn't been accepted
	// yet - both the inline "Invite to project" button (uninvited rows) and the
	// "Resend invite" row action (PENDING rows). The backend reuses the row's
	// current permissions/preset, so no payload is needed.
	const handleResendInvite = useCallback(
		async (member) => {
			if (!member?.id || !paramProjectId) return;
			try {
				setInvitingMemberId(member.id);
				await resendInvite({
					projectId: paramProjectId,
					memberId: member.id,
				}).unwrap();
				showToast(t("projects.members.invitations-sent"), {
					type: "success",
				});
			} catch (error) {
				showToast(
					error?.data?.message ||
						error?.message ||
						t("errors.general"),
					{ type: "error" },
				);
			} finally {
				setInvitingMemberId(null);
			}
		},
		[resendInvite, paramProjectId, t],
	);

	const onRevokeMember = async () => {
		if (!memberToRevoke) return;
		try {
			setRevokeLoading(true);
			await revokeMember({
				projectId: paramProjectId,
				memberId: memberToRevoke.id,
			}).unwrap();
			showToast(t("projects.members.revoke-success"), { type: "success" });
			setShowRevokeModal(false);
			setMemberToRevoke(null);
		} catch (error) {
			showToast(
				error?.data?.message || error?.message || t("errors.general"),
				{ type: "error" },
			);
		} finally {
			setRevokeLoading(false);
		}
	};

	const onDeleteMember = async () => {
		if (!memberToDelete) return;
		try {
			setDeleteLoading(true);
			await removeMember({
				projectId: paramProjectId,
				memberId: memberToDelete.id,
			}).unwrap();
			showToast(t("projects.members.member-removed-success"), {
				type: "success",
			});
			setShowDeleteModal(false);
			setMemberToDelete(null);
		} catch (error) {
			showToast(error?.data?.message || error?.message || t("errors.general"), { type: "error" });
		} finally {
			setDeleteLoading(false);
		}
	};

	const actionHandlers = useMemo(
		() => ({
			onEditPermissions: handleEditPermissions,
			onDelete: handleDelete,
			onRevoke: handleRevoke,
			onPromoteAdmin: handlePromoteAdmin,
			onDemoteAdmin: handleDemoteAdmin,
			onInviteProvisional: handleInviteProvisional,
			onResendInvite: handleResendInvite,
		}),
		[
			handleEditPermissions,
			handleDelete,
			handleRevoke,
			handlePromoteAdmin,
			handleDemoteAdmin,
			handleInviteProvisional,
			handleResendInvite,
		],
	);

	const memberColumns = useMemo(
		() =>
			getMemberColumns(t, actionHandlers, memberPerm, {
				categoryNamesById,
				onInvite: handleResendInvite,
				onInviteProvisional: handleInviteProvisional,
				invitingMemberId,
				canInvite: canInviteMembers,
			}),
		[
			t,
			actionHandlers,
			memberPerm,
			categoryNamesById,
			handleResendInvite,
			handleInviteProvisional,
			invitingMemberId,
			canInviteMembers,
		],
	);

	const onFilterAction = (index, key, info) => {
		const localFilterItems = [...filterItems];
		localFilterItems[index].value = info?.value;
		setFilterItems(localFilterItems);

		let filtered = [...projectMembers];
		localFilterItems.forEach((item) => {
			if (item.key === "search" && item.value) {
				filtered = searchData(item.value, filtered, memberColumns);
			}
		});
		setFilteredMembers(filtered);

		if (key === "add" && canInviteMembers) {
			setShowInviteWizard(true);
		}
		if (key === "refresh") {
			refetch();
		}
	};

	if (permissionLoading) {
		return (
			<div className="project-members-page">
				<ProjectHeader
					projectName={projectDetails?.name}
					tabTitle={t("projects.members.title")}
				/>
				<TableSkeleton />
			</div>
		);
	}

	if (!canViewMembers) {
		return (
			<div className="project-members-page">
				<ProjectHeader
					projectName={projectDetails?.name}
					tabTitle={t("projects.members.title")}
				/>
				<NoPermissionAlert />
			</div>
		);
	}

	return (
		<div className="project-members-page">
			<ProjectHeader
				projectName={projectDetails.name}
				tabTitle={t("projects.members.title")}
			/>
			<div className="page-container">
				<FilterBar
					items={filterItems}
					onFilterAction={onFilterAction}
					canAdd={canInviteMembers}
				/>

				<div className="flex-grow-1 ag-grid-parent">
					{isLoading && filteredMembers.length === 0 ? (
						<TableSkeleton />
					) : (
						<AgGridAtom
							columnDefs={memberColumns}
							rowData={filteredMembers}
							context={{ projectCan: can }}
							overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t("projects.members.no-members")}</span>`}
							defaultColDef={{
								flex: 1,
								sortable: true,
								filter: true,
							}}
						/>
					)}
				</div>

				<AddMemberWizard
					show={showInviteWizard}
					onClose={() => setShowInviteWizard(false)}
					projectId={paramProjectId}
					existingMemberIds={projectMembers
						.map((m) => m.userId ?? m.user?.id)
						.filter(Boolean)}
				/>

				<EditPermissionsModal
					show={showEditModal}
					onClose={() => {
						setShowEditModal(false);
						setMemberToEdit(null);
					}}
					member={memberToEdit}
					projectId={paramProjectId}
				/>

				<InviteProvisionalModal
					show={showInviteProvisionalModal}
					onClose={() => {
						setShowInviteProvisionalModal(false);
						setMemberToInviteProvisional(null);
					}}
					member={memberToInviteProvisional}
					projectId={paramProjectId}
				/>

				<ModalAtom
					show={showRevokeModal}
					handleClose={() => setShowRevokeModal(false)}
					title={t("projects.members.revoke-access")}
					saveText={revokeLoading ? "common.saving" : "projects.members.revoke-access"}
					handleSubmit={onRevokeMember}
					body={
						<p>
							{t("projects.members.revoke-confirm", {
								name: memberToRevoke?.user?.fullName || "",
							})}
						</p>
					}
				/>

				<ModalAtom
					show={showDeleteModal}
					handleClose={() => setShowDeleteModal(false)}
					title={t("projects.members.remove-member")}
					saveText={deleteLoading ? "common.saving" : "common.delete"}
					handleSubmit={onDeleteMember}
					body={
						<p>
							{t("projects.members.remove-member-warning", {
								name: memberToDelete?.user?.fullName || "",
							})}
						</p>
					}
				/>

				<DemoteAdminModal
					show={showDemoteModal}
					onClose={() => {
						setShowDemoteModal(false);
						setMemberToDemote(null);
					}}
					member={memberToDemote}
					projectId={paramProjectId}
				/>
			</div>
		</div>
	);
}

export default ProjectMembers;
